// Combined bin / container picker. Shows the tenant's own "Standard" container
// types (the generic CHEP / arm-lug / steel-cage set, plus "KF49 bin" only for a
// tenant that owns it) and any active custom bin_types from the registry —
// filtered by grower when one is supplied.
//
// Value is encoded as either 'enum:<CHEP_BIN>' or 'type:<uuid>'.
// Helpers below split it into the two columns we actually persist
// (container_type enum + bin_type_id FK).

import { useEffect } from 'react'
import { standardTypesForTenant, type ContainerType } from '@/lib/containerTypes'
import { useBinTypes, type BinTypeRow } from '@/hooks/useBinTypes'
import clsx from 'clsx'

export type ContainerPickerValue = string  // 'enum:KF49_BIN' | 'type:<uuid>'

export function encodeEnum(c: ContainerType): ContainerPickerValue {
  return `enum:${c}`
}
export function encodeType(id: string): ContainerPickerValue {
  return `type:${id}`
}

// Split the combined value into the two persisted columns. Custom
// types still need a container_type to satisfy the enum column —
// KF49_BIN is used as a generic stand-in (the bin_type_id is what
// the tare resolver and invoicing filter actually use).
export function decodeContainerPickerValue(
  v: ContainerPickerValue,
  // customTypes is no longer needed for the resolution itself, but the
  // signature is kept stable so callers don't churn — and so the picker
  // and its consumers fetch the same list.
  _customTypes: BinTypeRow[],
): { container_type: ContainerType; bin_type_id: string | null } {
  if (v.startsWith('type:')) {
    const id = v.slice(5)
    return { container_type: 'KF49_BIN', bin_type_id: id }
  }
  return { container_type: v.slice(5) as ContainerType, bin_type_id: null }
}

// Compute the picker's initial value from a row that already has
// (container_type, bin_type_id) — bin_type_id wins when set.
export function valueFromRow(row: {
  container_type: ContainerType | null
  bin_type_id?:   string | null
}): ContainerPickerValue {
  if (row.bin_type_id) return encodeType(row.bin_type_id)
  if (row.container_type) return encodeEnum(row.container_type)
  return encodeEnum('KF49_BIN')
}

interface ContainerTypePickerProps {
  value:     ContainerPickerValue
  onChange:  (v: ContainerPickerValue) => void
  growerId?: string | null
  /** Optional CSS classes for the underlying <select>. */
  className?: string
  /** Show a hint below when no grower is selected. */
  hintWhenNoGrower?: boolean
  /**
   * When set, render a leading empty option with this label (value '').
   * Lets callers offer a "not specified" choice — e.g. the delivery
   * "Fruit arrived in" picker, where '' means "use the default tare".
   */
  noneLabel?: string
}

export default function ContainerTypePicker(props: ContainerTypePickerProps) {
  const { data: customTypes = [] } = useBinTypes({
    onlyActive: true,
    growerId: props.growerId ?? null,
  })

  // Standard options are the generic enum filtered to THIS tenant's own
  // is_system_default mirrors — so a non-KF49 tenant doesn't get "KF49 bin",
  // while KF49 still does (from its own mirror row).
  const standardTypes = standardTypesForTenant(customTypes)
  const hasMirrors = customTypes.some(t => t.is_system_default)
  const codesKey = standardTypes.map(c => c.value).join('|')

  // Many callers hard-default the value to enum:KF49_BIN. Once the tenant's bin
  // types have loaded, if the selected enum isn't one this tenant offers (e.g.
  // KF49_BIN for a non-KF49 tenant) snap it to the first available standard so
  // the <select> can't sit on a value with no matching option. Custom (type:)
  // values and the explicit "none" ('') are left alone. Saved rows for a tenant
  // never carry a code outside its own set, so this only rewrites stale defaults.
  // fallow-ignore-next-line complexity  (guard-heavy effect; CRAP inflated by 0 UI coverage)
  useEffect(() => {
    if (!hasMirrors || !standardTypes.length) return
    if (props.value === '' || props.value.startsWith('type:')) return
    const code = props.value.startsWith('enum:') ? props.value.slice(5) : props.value
    if (!standardTypes.some(c => c.value === code)) {
      props.onChange(encodeEnum(standardTypes[0].value))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMirrors, codesKey, props.value])

  return (
    <>
      <select
        aria-label="Container type"
        value={props.value}
        onChange={e => props.onChange(e.target.value)}
        className={clsx('field text-sm', props.className)}>
        {props.noneLabel && <option value="">{props.noneLabel}</option>}
        <optgroup label="Standard">
          {standardTypes.map(c => (
            <option key={c.value} value={encodeEnum(c.value)}>{c.label}</option>
          ))}
        </optgroup>
        {/* System-default rows mirror the enum types under Bin Inventory's
            Bin Types tab so admins can edit per-type tares (mig 285). Hide
            them here to avoid duplicating the Standard optgroup above. */}
        {customTypes.filter(t => !t.is_system_default).length > 0 && (
          <optgroup label={props.growerId ? 'Grower bin types' : 'Custom bin types'}>
            {customTypes.filter(t => !t.is_system_default).map(t => (
              <option key={t.bin_type_id} value={encodeType(t.bin_type_id)}>
                {t.label} ({t.tare_kg.toFixed(1)} kg)
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {props.hintWhenNoGrower && !props.growerId && (
        <p className="text-[10px] text-gray-500 mt-1">Pick a grower above to see their custom bin types here.</p>
      )}
    </>
  )
}

// Re-export the row type for callers that need the customTypes list
// alongside the picker (decode on save, etc.).
export { useBinTypes } from '@/hooks/useBinTypes'
export type { BinTypeRow } from '@/hooks/useBinTypes'
